import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { memo, useContext, useMemo } from 'react';
import { Switch, Route, useLocation, useHistory } from 'react-router-dom';
import {
  AttachMoneyOutlined,
  DescriptionOutlined,
  AppSettingsAltOutlined,
  ArticleOutlined,
  HowToRegOutlined,
  ForwardToInboxOutlined
} from '@mui/icons-material';

import { IconType } from '../../../common/icon';
import { ClientStatusContext } from '../../../common/client-status';

import Tab from './tab';
import { getAvailableSubpages } from './utils';
import { SubPageItem, Subpages, IconsDictionary } from './interfaces';

const useStyles = makeStyles((theme: Theme) => ({
  withSubpagesContainer: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexFlow: 'column'
  },
  pageNavigation: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  pageTitle: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    color: theme.palette.common.black,
    margin: theme.spacing(0, 4, 1, 4),
    fontSize: '22px'
  },
  tabs: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  contentContainer: {
    width: '100%',
    flex: '1 1 auto'
  }
}));

const iconsDictionary: IconsDictionary[] = [
  { title: 'Billing', icon: AttachMoneyOutlined },
  { title: 'Reports', icon: DescriptionOutlined },
  { title: 'Apps', icon: AppSettingsAltOutlined },
  { title: 'Transaction history', icon: AttachMoneyOutlined },
  { title: 'Active users', icon: HowToRegOutlined },
  { title: 'Invited users', icon: ForwardToInboxOutlined }
];

const iconTranslator = (title: Subpages): IconType =>
  iconsDictionary.find((entry: IconsDictionary) => entry.title === title)?.icon || ArticleOutlined;

interface Props {
  title: string;
  items: SubPageItem[];
  mainPagePath: string;
}

const WithSubpages = ({ items, title, mainPagePath }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { pathname } = useLocation();
  const { checkIfUserHasApiPrivilege, checkIfUserHasPortalPrivilege } = useContext(ClientStatusContext);

  const filteredMenuItems = useMemo(
    () => getAvailableSubpages(checkIfUserHasPortalPrivilege, checkIfUserHasApiPrivilege, undefined, items),
    [checkIfUserHasApiPrivilege, checkIfUserHasPortalPrivilege, items]
  );

  const tabs = useMemo(
    () =>
      filteredMenuItems.map((tab, index) => (
        <Tab key={index} title={tab.title} icon={iconTranslator(tab.title as Subpages)} path={tab.path} />
      )),
    [filteredMenuItems]
  );

  if (pathname === mainPagePath && filteredMenuItems.length > 0) {
    history.push(filteredMenuItems[0].path);
  }

  return (
    <Box className={classes.withSubpagesContainer} data-testid="with-subpages">
      <Box className={classes.pageNavigation}>
        <Box className={classes.pageTitle}>{title}</Box>
        <Box className={classes.tabs}>{tabs}</Box>
      </Box>
      <Box className={classes.contentContainer}>
        <Switch>
          {filteredMenuItems.map((subpage, index) => (
            <Route key={index} path={subpage.path} render={subpage.render} />
          ))}
        </Switch>
      </Box>
    </Box>
  );
};

export default memo(WithSubpages);
